import { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAgentGroups } from '@/features/agent-groups/api';
import type { TtsFlowFormValues, TtsPatchRow } from '../../types';

interface Props {
  values: TtsFlowFormValues;
  setValues: (patch: Partial<TtsFlowFormValues>) => void;
}

const DTMF_DIGITS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];

export function StepPatch({ values, setValues }: Props) {
  const groups = useAgentGroups();

  const [draftGroupId, setDraftGroupId] = useState('');
  const [draftDtmf, setDraftDtmf] = useState('');

  const taken = new Set(values.patchList.map((r) => r.agentDtmf));
  const availableDtmf = DTMF_DIGITS.filter((d) => !taken.has(d));

  const addRow = () => {
    if (!draftGroupId || !draftDtmf) {
      toast.error('Pick a group and a DTMF.');
      return;
    }
    const group = (groups.data ?? []).find(
      (g) => String(g.groupId) === draftGroupId,
    );
    if (!group) return;
    const row: TtsPatchRow = {
      groupId: draftGroupId,
      groupName: group.groupName,
      agentDtmf: draftDtmf,
    };
    setValues({ patchList: [...values.patchList, row] });
    setDraftGroupId('');
    setDraftDtmf('');
  };

  const removeRow = (dtmf: string) =>
    setValues({
      patchList: values.patchList.filter((r) => r.agentDtmf !== dtmf),
    });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Patch agent groups</CardTitle>
        <CardDescription>
          Map each DTMF digit to the agent group the call should patch to.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 gap-3 rounded-md border border-border p-3 sm:grid-cols-[1fr_120px_auto] sm:items-end">
          <div className="space-y-1.5">
            <Label>
              Agent group
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <Select value={draftGroupId} onValueChange={setDraftGroupId}>
              <SelectTrigger>
                <SelectValue
                  placeholder={
                    groups.isLoading
                      ? 'Loading…'
                      : (groups.data ?? []).length === 0
                        ? 'No agent groups — create one first'
                        : 'Pick a group'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {(groups.data ?? []).map((g) => (
                  <SelectItem key={g.groupId} value={String(g.groupId)}>
                    {g.groupName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>
              DTMF
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <Select
              value={draftDtmf}
              onValueChange={setDraftDtmf}
              disabled={availableDtmf.length === 0}
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={
                    availableDtmf.length === 0 ? 'All digits used' : 'Digit'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {availableDtmf.map((d) => (
                  <SelectItem key={d} value={d}>
                    {d}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            type="button"
            onClick={addRow}
            disabled={!draftGroupId || !draftDtmf}
          >
            <Plus className="h-4 w-4" />
            Bind
          </Button>
        </div>

        {values.patchList.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border bg-muted/20 py-6 text-center text-sm text-muted-foreground">
            No bindings yet. Bind at least one DTMF digit to an agent group.
          </div>
        ) : (
          <ol className="space-y-2">
            {values.patchList.map((r) => (
              <li
                key={r.agentDtmf}
                className="flex items-center gap-3 rounded-lg border border-border bg-card p-3"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-base font-bold text-primary-foreground">
                  {r.agentDtmf}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-foreground">
                    {r.groupName}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Group #{r.groupId}
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-destructive hover:text-destructive"
                  onClick={() => removeRow(r.agentDtmf)}
                  aria-label="Remove binding"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </li>
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}
